/**
 * PortalImportDialog — what the challan import is doing, while it does it.
 *
 * The import signs in to the e-filing portal and walks its payment history
 * in a window placed out of the way, because there is nothing there to
 * watch and nothing to fill in. That leaves this page as the only place
 * the run is visible at all — and a button that appears to do nothing for
 * two minutes is the reading anybody would reach.
 *
 * So: it blocks, it says what step the extension is on, and it says the
 * wait is normal. It cannot be dismissed by clicking away, because the run
 * carries on either way and a dialog that vanishes on a stray click takes
 * the only progress indicator with it.
 */
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const SPINNER_CSS = `
@keyframes pid-spin { to { transform: rotate(360deg); } }
.pid-spinner {
  width: 38px; height: 38px; border-radius: 50%;
  border: 3px solid var(--gt-surface-2);
  border-top-color: var(--gt-primary);
  animation: pid-spin 820ms linear infinite;
}
@media (prefers-reduced-motion: reduce) {
  /* A spinner is decoration; the words below it carry the meaning. */
  .pid-spinner { animation-duration: 3s; }
}
.pid-body { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 18px 4px 6px; }
.pid-step {
  font-size: 12px; color: var(--gt-text-secondary);
  text-align: center; min-height: 34px; max-width: 360px; line-height: 1.5;
}
.pid-note { font-size: 11.5px; color: var(--gt-text-tertiary); text-align: center; }
`;

export default function PortalImportDialog({
  open,
  status,
  onHide,
}: {
  open: boolean;
  /** The extension's own last line. The only sign the run is still moving. */
  status: string;
  /** Lets somebody put it away. The run is unaffected — it is in another
   *  window — and the result lands whether this is on screen or not. */
  onHide: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onHide()}>
      <DialogContent style={{ maxWidth: 420 }}>
        <style>{SPINNER_CSS}</style>
        <DialogHeader>
          <DialogTitle>Importing challan data</DialogTitle>
          <DialogDescription>
            Reading what the e-filing portal says it was paid.
          </DialogDescription>
        </DialogHeader>

        <div className="pid-body">
          <div className="pid-spinner" aria-hidden />
          <p className="pid-step" role="status" aria-live="polite">
            {status || "Opening the portal in a background window…"}
          </p>
          <p className="pid-note">
            This takes a couple of minutes. The portal window stays out of the
            way — it comes forward by itself if it needs you.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
